import React, { useState, useRef, useEffect } from 'react';
import { FiX } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Cropper from 'react-easy-crop';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';

type Point = { x: number; y: number };
type Area = { x: number; y: number; width: number; height: number };

interface ChangePhotoModalProps {
  onClose: () => void;
  onSave: (photoUrl: string) => void;
  currentPhotoUrl?: string;
}

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const CropperComponent = ((Cropper as any)?.default ?? Cropper) as React.ComponentType<any>;

const ChangePhotoModal: React.FC<ChangePhotoModalProps> = ({
  onClose,
  onSave,
  currentPhotoUrl,
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(currentPhotoUrl || null);
  const [fileName, setFileName] = useState<string>(currentPhotoUrl ? 'Current photo' : 'No file selected');
  const [hasChanges, setHasChanges] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCropping, setIsCropping] = useState(false);
  const [croppedPhoto, setCroppedPhoto] = useState<string | null>(null);

  // Cropper states
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // const imageRef = useRef<HTMLImageElement | null>(null);

  // Helper function to truncate filename
  const truncateFileName = (name: string, maxLength: number = 20) => {
    if (name.length <= maxLength) return name;

    const lastDotIndex = name.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? name.substring(lastDotIndex) : '';
    const nameWithoutExt = lastDotIndex !== -1 ? name.substring(0, lastDotIndex) : name;

    const truncatedName = nameWithoutExt.substring(0, maxLength - 3 - extension.length);
    return `${truncatedName}...${extension}`;
  };

  // Update state when currentPhotoUrl changes
  useEffect(() => {
    if (currentPhotoUrl) {
      setSelectedPhoto(currentPhotoUrl);
      setFileName('Current photo');
      setCroppedPhoto(null);
    }
  }, [currentPhotoUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG, PNG, or WebP images are allowed');
        return;
      }

      setFileName(file.name);
      setHasChanges(true);
      setIsCropping(true);

      const reader = new FileReader();
      reader.onload = () => {
        setSelectedPhoto(reader.result as string);
        setCroppedPhoto(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const getCroppedImage = async (): Promise<string | null> => {
    if (!selectedPhoto || !croppedAreaPixels) return null;

    const image = new Image();
    image.src = selectedPhoto;

    return new Promise((resolve, reject) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        const { x, y, width, height } = croppedAreaPixels;
        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(image, x, y, width, height, 0, 0, width, height);

        try {
          const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.95);
          resolve(croppedDataUrl);
        } catch (error) {
          reject(error);
        }
      };
      image.onerror = () => reject(new Error('Failed to load image'));
    });
  };

  const handleSave = async () => {
    if (!hasChanges || isUploading) return;

    setIsUploading(true);

    try {
      let finalPhoto = croppedPhoto || selectedPhoto;

      if (isCropping && croppedAreaPixels) {
        const croppedImage = await getCroppedImage();
        if (croppedImage) {
          finalPhoto = croppedImage;
          setCroppedPhoto(croppedImage);
        }
      }

      let fileToUpload: File | null = null;

      if (finalPhoto && finalPhoto.startsWith('data:image')) {
        const response = await fetch(finalPhoto);
        const blob = await response.blob();
        const fileExtension = blob.type.split('/')[1] || 'jpg';
        const originalFileName = fileInputRef.current?.files?.[0]?.name || `profile.${fileExtension}`;
        fileToUpload = new File([blob], originalFileName, { type: blob.type });
      } else if (fileInputRef.current?.files?.[0]) {
        fileToUpload = fileInputRef.current.files[0];
      }

      if (!fileToUpload) {
        toast.error('No image to upload');
        setIsUploading(false);
        return;
      }

      // Use common JWT utility
      const { token, tenantId, userId } = getAuthContext();

      if (!token) {
        toast.error('Authentication token not found');
        setIsUploading(false);
        return;
      }


      const formData = new FormData();
      formData.append('file', fileToUpload);

      const response = await fetch(`${BASE_URL}/v1/profile/me/photo`, {
        method: 'POST',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to upload photo');
      }

      const result = await response.json();
      const newPhotoUrl = result?.data?.photoUrl || result?.photoUrl || finalPhoto!;

      toast.success('Profile photo updated successfully');
      onSave(newPhotoUrl);
      onClose();
    } catch (error: any) {
      // console.error('Photo upload failed:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedPhoto(currentPhotoUrl || null);
    setFileName(currentPhotoUrl ? 'Current photo' : 'No file selected');
    setHasChanges(false);
    setIsCropping(false);
    setCroppedPhoto(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleChangePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const getDisplayFileName = () => {
    if (fileName === 'Current photo' || fileName === 'No file selected') {
      return fileName;
    }
    return truncateFileName(fileName, 20);
  };

  return (
    <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
      <div className='bg-white rounded-lg shadow-lg p-4 relative' style={{ width: '476px', minHeight: '433px' }}>
        <button onClick={onClose} className='absolute top-4 right-4 text-gray-500 hover:text-gray-700'>
          <FiXComponent size={20} />
        </button>

        <h2 className='text-lg font-semibold mb-2'>Change Photo</h2>
        <p className='text-sm text-gray-500 mb-4'>Choose and crop an image</p>

        <div className='flex justify-center mb-4' style={{ height: '270px', alignItems: 'center' }}>
          <div className='relative rounded-full overflow-hidden' style={{ width: '15rem', height: '15rem', background: 'linear-gradient(135deg, #E1EAF0, #F5F8FA)' }}>
            {isCropping && selectedPhoto && !croppedPhoto ? (
              <div className='relative w-full h-full'>
                <CropperComponent
                  image={selectedPhoto}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                  showGrid={false}
                  style={{
                    containerStyle: { width: '100%', height: '100%', position: 'relative' },
                    mediaStyle: { borderRadius: '50%' },
                  }}
                />
              </div>
            ) : (
              <>
                {(croppedPhoto || selectedPhoto) ? (
                  <img
                    src={croppedPhoto || selectedPhoto || undefined}
                    alt='Preview'
                    className='w-full h-full object-cover'
                  />
                ) : (
                  <div className='flex items-center justify-center h-full text-gray-400'>No image selected</div>
                )}
              </>
            )}
          </div>
        </div>

        {isCropping && selectedPhoto && !croppedPhoto && (
          <div className='flex items-center gap-3 px-4 mb-4'>
            <span className='text-xs text-gray-500'>−</span>
            <input
              type='range'
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className='flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-cyan-500'
              style={{
                background: `linear-gradient(to right, #00A4BD 0%, #00A4BD ${((zoom - 1) / 2) * 100}%, #E5E7EB ${((zoom - 1) / 2) * 100}%, #E5E7EB 100%)`,
              }}
            />
            <span className='text-xs text-gray-500'>+</span>
            <span className='text-xs text-gray-500 min-w-[32px] text-center'>
              {Math.round(zoom * 100)}%
            </span>
          </div>
        )}

        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center' style={{ maxWidth: '65%' }}>
            <button
              onClick={handleChangePhotoClick}
              className='flex items-center gap-1 text-cyan-600 border border-cyan-500 rounded px-3 py-2 text-sm hover:bg-cyan-50 whitespace-nowrap'
              style={{ color: '#00A4BD', borderColor: '#00A4BD' }}
            >
              + Change Photo
            </button>
            <input
              type='file'
              accept='image/jpeg,image/png,image/webp'
              ref={fileInputRef}
              onChange={handleFileChange}
              className='hidden'
            />
            <span className='text-sm text-gray-500 ml-3 truncate' style={{ maxWidth: '150px' }} title={fileName}>
              {getDisplayFileName()}
            </span>
          </div>

          <div className='flex gap-2'>
            <button
              onClick={handleCancel}
              disabled={!hasChanges || isUploading}
              className={`px-3 py-1.5 border rounded whitespace-nowrap ${hasChanges && !isUploading
                ? 'border-orange-500 text-orange-500 hover:bg-orange-50 cursor-pointer'
                : 'border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isUploading}
              className={`px-3 py-1.5 rounded text-white whitespace-nowrap ${hasChanges && !isUploading
                ? 'bg-orange-500 hover:bg-orange-600 cursor-pointer'
                : 'bg-gray-300 cursor-not-allowed'
                }`}
            >
              {isUploading ? 'Uploading...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePhotoModal;